// Project Manager → one batch's report (/pm/projects/:id/:kind/:batchId).
//
// The PM reads this; they don't mark fixes (that's the developer's judgement,
// and a PM overriding it would break the "dev claims → re-run verifies" loop).
// Same table, same numbers, one flag different.

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, AlertTriangle, Boxes, RefreshCw } from "lucide-react";
import BatchReportTable from "@/components/shared/BatchReportTable";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import { useSurfaceBase } from "@/hooks/useSurfaceBase";
import { getExeBatch, pageExeBatchItems } from "@/api/exe";
import { getExtBatch, pageExtBatchItems } from "@/api/extension";
import { formatDateTime } from "@/lib/dates";

const fmtWhen = (v) => {
  if (!v) return "—";
  return formatDateTime(v);
};

export default function PmBatchReport() {
  // These screens are shared with the company Admin surface, so links
  // must stay inside whichever root the reader came in through.
  const base = useSurfaceBase();
  const { id, kind, batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isExe = kind === "exe";

  const loadBatch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setBatch(await (isExe ? getExeBatch : getExtBatch)(batchId));
    } catch (e) {
      setError((e && e.message) || "That report isn't available.");
    } finally {
      setLoading(false);
    }
  }, [isExe, batchId]);

  useEffect(() => { loadBatch(); }, [loadBatch]);

  // Stable identity so BatchReportTable's loader doesn't re-fire every render.
  const fetchPage = useCallback(
    (params) => (isExe ? pageExeBatchItems : pageExtBatchItems)(batchId, params),
    [isExe, batchId],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-20 text-slate-400">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="card p-12 text-center">
        <AlertTriangle size={28} className="mx-auto mb-3 text-amber-500" />
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {error || "That report isn't available."}
        </p>
        <Button className="mt-6" variant="outline" onClick={() => navigate(`${base}/projects/${id}`)}>
          <ArrowLeft size={15} /> Back to the project
        </Button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => navigate(`${base}/projects/${id}`)}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-white"
      >
        <ArrowLeft size={15} /> Back to the project
      </button>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            <Boxes size={22} className="text-brand-500" />
            {batch.name || `Batch ${String(batchId).slice(0, 8)}`}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {fmtWhen(batch.created_at)}
            {batch.completed_at ? ` → ${fmtWhen(batch.completed_at)}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={batch.status} />
          <Badge tone="slate" className="capitalize">{kind}</Badge>
          <Badge tone="emerald">{batch.passed || 0} passed</Badge>
          <Badge tone="rose">{batch.failed || 0} failed</Badge>
          {!!batch.errored && <Badge tone="amber">{batch.errored} errored</Badge>}
          {/* First-class buckets the runners serve and deliberately do NOT fold
              into failed/errored — omitting them under-reported the batch. */}
          {!!batch.cancelled && <Badge tone="slate">{batch.cancelled} cancelled</Badge>}
          {!!batch.inconclusive && <Badge tone="slate">{batch.inconclusive} inconclusive</Badge>}
          {!!batch.orphaned && <Badge tone="slate">{batch.orphaned} orphaned</Badge>}
          {!!batch.rerun_count && (
            <Badge tone="sky">re-run ×{batch.rerun_count}</Badge>
          )}
          {/* A running batch showed frozen counts forever with no way to update. */}
          <Button variant="outline" size="sm" onClick={loadBatch} title="Refresh these numbers">
            <RefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      <BatchReportTable
        kind={kind}
        batchId={batchId}
        fetchPage={fetchPage}
        canJudge={false}
        projectId={id}
        onChanged={loadBatch}
      />
    </>
  );
}
